import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useBlocker } from "react-router-dom";
import { useConfirm } from "@/components/confirm-dialog";

type Register = (id: string, dirty: boolean) => void;

const UnsavedContext = createContext<Register | null>(null);

/**
 * Ein einziger Wächter für die ganze App (React Router erlaubt nur einen
 * Blocker). Formulare melden sich per useUnsavedChanges(dirty) an; sobald
 * eines davon ungespeicherte Änderungen hat, wird beim Verlassen gewarnt –
 * in der App per Dialog, beim Schließen/Neuladen des Tabs über den Browser.
 */
export function UnsavedChangesGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  const confirm = useConfirm();
  const [dirtyIds, setDirtyIds] = useState<ReadonlySet<string>>(new Set());
  const anyDirty = dirtyIds.size > 0;

  const register = useCallback<Register>((id, dirty) => {
    setDirtyIds((prev) => {
      if (prev.has(id) === dirty) return prev;
      const next = new Set(prev);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      anyDirty && currentLocation.pathname !== nextLocation.pathname,
  );
  // Nur auf den Wechsel nach "blocked" reagieren, nicht auf jedes neue
  // Blocker-Objekt – sonst ginge der Dialog bei Re-Renders erneut auf.
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    let active = true;
    confirm({
      title: "Ungespeicherte Änderungen verwerfen?",
      description:
        "Du hast Änderungen, die noch nicht gespeichert sind. Wenn du die Seite verlässt, gehen sie verloren.",
      confirmLabel: "Verwerfen und verlassen",
      cancelLabel: "Auf der Seite bleiben",
      destructive: true,
    }).then((ok) => {
      if (!active) return;
      if (ok) {
        setDirtyIds(new Set());
        blockerRef.current.proceed?.();
      } else {
        blockerRef.current.reset?.();
      }
    });
    return () => {
      active = false;
    };
  }, [blocker.state, confirm]);

  useEffect(() => {
    if (!anyDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [anyDirty]);

  return (
    <UnsavedContext.Provider value={register}>
      {children}
    </UnsavedContext.Provider>
  );
}

/** Meldet ein Formular mit ungespeicherten Änderungen beim Wächter an. */
export function useUnsavedChanges(dirty: boolean) {
  const register = useContext(UnsavedContext);
  const id = useId();
  useEffect(() => {
    register?.(id, dirty);
  }, [register, id, dirty]);
  useEffect(() => () => register?.(id, false), [register, id]);
}
